import {
  type DynamicClient,
  getActiveNetworkData,
} from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  bufferToBase64,
  emitEvent,
  filterDuplicates,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
  getActiveNetworkIdFromLastKnownRegistry,
  getWalletProviderRegistry,
  switchActiveNetworkInLastKnownRegistry,
} from '@dynamic-labs-sdk/client/core';
import { SessionClosedUnexpectedlyError } from '@dynamic-labs-sdk/wallet-connect';
import {
  createWalletConnectSessionEventEmitter,
  getSignClient,
  splitCaip10Token,
} from '@dynamic-labs-sdk/wallet-connect/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import { PublicKey, Transaction, VersionedTransaction } from '@solana/web3.js';
import { SDK_ERRORS } from '@walletconnect/utils';
import bs58 from 'bs58';

import type { SolanaWalletAccount } from '../../SolanaWalletAccount.types';
import { isVersionedTransaction } from '../../utils/isVersionedTransaction';
import { solanaExecuteCheckoutTransaction } from '../../utils/solanaExecuteCheckoutTransaction';
import { solanaTransferAmount } from '../../utils/solanaTransferAmount';
import type { solanaMethods } from '../consts';
import type { WalletConnectSolanaWalletProvider } from '../walletConnectSolanaWalletProvider.types';
import type {
  AvailableSolanaSessionRequests,
  SolanaSessionRequest,
  SolanaSessionRequestResultMap,
} from './walletConnectSolanaSessionRequest.types';

type createWalletProviderFromWalletConnectSolanaSessionParams = {
  sessionTopic: string;
};

/**
 * The list of Solana request methods that require user action.
 * We will raise a user action requested event for these methods.
 */
const methodsThatRequireUserAction: string[] = [
  'solana_signMessage',
  'solana_signTransaction',
  'solana_signAllTransactions',
  'solana_signAndSendTransaction',
] satisfies (typeof solanaMethods)[number][];

export const createWalletProviderFromWalletConnectSolanaSession = async (
  { sessionTopic }: createWalletProviderFromWalletConnectSolanaSessionParams,
  client: DynamicClient
): Promise<WalletConnectSolanaWalletProvider> => {
  const signClient = await getSignClient(client);

  const getSession = () => {
    const session = signClient.session.get(sessionTopic);

    if (!session) {
      void disconnect();

      throw new SessionClosedUnexpectedlyError({
        sessionTopic,
        walletDisplayName: displayName,
      });
    }

    return session;
  };

  const displayName = getSession().peer.metadata.name;

  const namespaceKey = 'solana';

  const walletProviderType = WalletProviderEnum.WalletConnect;

  const key = formatWalletProviderKey({
    chain: 'SOL',
    displayName,
    suffix: sessionTopic,
    walletProviderType,
  });

  const getActiveNetworkId: WalletConnectSolanaWalletProvider['getActiveNetworkId'] =
    async () => {
      // Assert the session is still valid
      getSession();

      return await getActiveNetworkIdFromLastKnownRegistry({
        client,
        walletProviderKey: key,
      });
    };

  const switchActiveNetwork: WalletConnectSolanaWalletProvider['switchActiveNetwork'] =
    async ({ networkId }) => {
      // Assert the session is still valid
      getSession();

      await switchActiveNetworkInLastKnownRegistry({
        client,
        networkId,
        walletProviderKey: key,
      });
    };

  const disconnect: NonNullable<
    WalletConnectSolanaWalletProvider['disconnect']
  > = async () => {
    await signClient.disconnect({
      reason: SDK_ERRORS.USER_DISCONNECTED,
      topic: sessionTopic,
    });

    const walletProviderRegistry = getWalletProviderRegistry(client);

    walletProviderRegistry.unregister(key);
  };

  /**
   * We actually clear up the entire WC session and its wallet provider
   */
  const disconnectWalletAccount: WalletConnectSolanaWalletProvider['disconnectWalletAccount'] =
    async () => disconnect();

  /**
   * Helper function to make WalletConnect requests
   */
  const makeRequest = async <T extends AvailableSolanaSessionRequests>(
    request: SolanaSessionRequest<T>,
    walletAccount: SolanaWalletAccount
  ): Promise<SolanaSessionRequestResultMap[T]> => {
    assertDefined(walletAccount, 'Wallet account is required');

    const session = getSession();

    const peerMetadata = session.peer.metadata;

    if (methodsThatRequireUserAction.includes(request.method)) {
      emitEvent(
        {
          args: {
            walletMetadata: peerMetadata,
            walletProviderKey: key,
          },
          event: 'walletConnectUserActionRequested',
        },
        client
      );
    }

    const { networkData } = await getActiveNetworkData(
      { walletAccount },
      client
    );

    assertDefined(
      networkData,
      `Failed to get network data for wallet account ${walletAccount.address}`
    );

    return signClient.request<SolanaSessionRequestResultMap[T]>({
      chainId: `${namespaceKey}:${networkData.genesisHash}`,
      request,
      topic: sessionTopic,
    });
  };

  /**
   * Serializes a transaction for WalletConnect
   * Reference: https://github.com/reown-com/appkit/blob/552b6b39b46e6c604b4d7d290634b9fa96bf7ed0/packages/adapters/solana/src/providers/AuthProvider.ts#L193
   */
  const serializeTransaction = (
    transaction: Transaction | VersionedTransaction
  ) => {
    const serialized = transaction.serialize({
      requireAllSignatures: false,
      verifySignatures: false,
    });

    return bs58.encode(new Uint8Array(serialized));
  };

  const signMessage: WalletConnectSolanaWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
  }) => {
    assertDefined(walletAccount, 'Wallet account is required');

    const publicKey = new PublicKey(walletAccount.address);

    const { signature } = await makeRequest(
      {
        method: 'solana_signMessage',
        params: { message, pubkey: publicKey.toBase58() },
      },
      walletAccount
    );

    const signatureBytes = bs58.decode(signature);

    return {
      signature: bufferToBase64(signatureBytes),
    };
  };

  const getConnectedAddresses: WalletConnectSolanaWalletProvider['getConnectedAddresses'] =
    async () => {
      const addresses = filterDuplicates(
        getSession().namespaces[namespaceKey].accounts.map(
          (account) => splitCaip10Token(account).address
        )
      );

      return { addresses };
    };

  const { cleanupEventEmitter, getEventEmitter } =
    createWalletConnectSessionEventEmitter({
      getSession,
      namespaceKey,
      sessionTopic,
      signClient,
    });

  const terminate: WalletConnectSolanaWalletProvider['terminate'] = async () =>
    cleanupEventEmitter();

  const signTransaction: WalletConnectSolanaWalletProvider['signTransaction'] =
    async ({ transaction, walletAccount }) => {
      assertDefined(walletAccount, 'Wallet account is required');

      type T = typeof transaction;

      const serializedTransaction = serializeTransaction(transaction);

      // Reference: https://github.com/reown-com/appkit/blob/552b6b39b46e6c604b4d7d290634b9fa96bf7ed0/packages/adapters/solana/src/providers/AuthProvider.ts#L98
      const { transaction: signedTransaction } = await makeRequest(
        {
          method: 'solana_signTransaction',
          params: { transaction: serializedTransaction },
        },
        walletAccount
      );

      const decodedTransaction = bs58.decode(signedTransaction);

      if (isVersionedTransaction(transaction)) {
        return {
          signedTransaction: VersionedTransaction.deserialize(
            decodedTransaction
          ) as T,
        };
      }

      return { signedTransaction: Transaction.from(decodedTransaction) as T };
    };

  const signAllTransactions: WalletConnectSolanaWalletProvider['signAllTransactions'] =
    async ({ transactions, walletAccount }) => {
      assertDefined(walletAccount, 'Wallet account is required');

      type T = (typeof transactions)[number];

      const serializedTransactions = transactions.map(serializeTransaction);

      // Reference: https://github.com/reown-com/appkit/blob/552b6b39b46e6c604b4d7d290634b9fa96bf7ed0/packages/adapters/solana/src/providers/AuthProvider.ts#L143
      const { transactions: signedTransactions } = await makeRequest(
        {
          method: 'solana_signAllTransactions',
          params: { transactions: serializedTransactions },
        },
        walletAccount
      );

      const signedTransactionsArray = signedTransactions.map(
        (signedTransaction, index) => {
          const originalTransaction = transactions[index];

          const decodedTransaction = bs58.decode(signedTransaction);

          if (isVersionedTransaction(originalTransaction)) {
            return VersionedTransaction.deserialize(decodedTransaction) as T;
          }

          return Transaction.from(decodedTransaction) as T;
        }
      );

      return { signedTransactions: signedTransactionsArray };
    };

  const signAndSendTransaction: WalletConnectSolanaWalletProvider['signAndSendTransaction'] =
    async ({ transaction, walletAccount }) => {
      assertDefined(walletAccount, 'Wallet account is required');

      const serializedTransaction = serializeTransaction(transaction);

      // Reference: https://github.com/reown-com/appkit/blob/552b6b39b46e6c604b4d7d290634b9fa96bf7ed0/packages/adapters/solana/src/providers/AuthProvider.ts#L114
      const { signature } = await makeRequest(
        {
          method: 'solana_signAndSendTransaction',
          params: { transaction: serializedTransaction },
        },
        walletAccount
      );

      return { signature };
    };

  return {
    chain: 'SOL',
    disconnect,
    disconnectWalletAccount,
    get events() {
      return getEventEmitter();
    },
    executeCheckoutTransaction: (args) =>
      solanaExecuteCheckoutTransaction(args, client),
    getActiveNetworkId,
    getConnectedAddresses,
    getSession,
    groupKey: formatWalletProviderGroupKey(displayName),
    key,
    metadata: {
      displayName,
      icon: getSession().peer.metadata.icons[0],
    },
    namespaceKey,
    signAllTransactions,
    signAndSendTransaction,
    signMessage,
    signTransaction,
    switchActiveNetwork,
    terminate,
    transferAmount: (args) => solanaTransferAmount(args, client),
    walletProviderType,
  };
};
